import type { ModStatusPagesParams } from "@common/api/aircraft"
import type {
  AppAircraftModStatusSchemasAircraftAssociation,
  ApplicabilityStatus,
  ModDataSource,
  ModificationType,
  ModNature,
  ModStatusEnum,
  ModStatusPublic,
  SbComplianceType,
} from "@common/client"
import type { FilterOptionValue } from "@common/components/filters/filter-config"
import {
  periodRange,
  periodSchema,
} from "@common/components/filters/period-model"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import { withFixed } from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"

export const MOD_STATUS_PANES: string[] = [
  "/_layout/aircraft_/$aircraftId/mod-status/new",
]

export const MOD_COLUMNS = [
  "number",
  "title",
  "sb",
  "status",
  "compliance",
  "ata",
  "embodied",
  "updated",
] as const

export const modColumnSchema = z.enum(MOD_COLUMNS)

export type ModColumn = z.infer<typeof modColumnSchema>

export const MOD_COLUMN_LABELS: Record<ModColumn, string> = {
  number: "Mod no.",
  title: "Title",
  sb: "SB no.",
  status: "Status",
  compliance: "Compliance",
  ata: "ATA",
  embodied: "Embodied",
  updated: "Updated",
}

export const MOD_SIZING: ColumnSizing<ModColumn> = {
  id: "aircraft-mod-status",
  widths: {
    number: 8,
    title: 20,
    sb: 9,
    status: 11,
    compliance: 7,
    ata: 5,
    embodied: 7,
    updated: 7,
  },
  fill: "title",
  extras: [],
  dropOrder: ["ata", "compliance", "updated", "embodied", "sb", "status"],
}

export const MOD_STATUS_LABELS: Record<ModStatusEnum, string> = {
  SB_SCHEDULED: "SB scheduled",
  SB_REJECTED: "SB rejected",
  SB_FULLY_EMBODIED: "Fully embodied",
  SB_PARTIALLY_EMBODIED: "Partially embodied",
  SB_NOT_YET_ISSUED: "SB not yet issued",
  MOD_NOT_APPLICABLE_IN_SB_SOLUTION: "Not applicable in SB solution",
  MOD_DONE_IN_PRODUCTION: "Done in production",
  MOD_PLANNED_IN_PRODUCTION: "Planned in production",
  EQUIVALENT_MOD_FACTORY_INCORPORATED: "Equivalent mod factory-incorporated",
}

export const COMPLIANCE_TYPE_LABELS: Record<SbComplianceType, string> = {
  MANDATORY: "Mandatory",
  OPTIONAL: "Optional",
  DESIRABLE: "Desirable",
  RECOMMENDED: "Recommended",
  IN_ABEYANCE: "In abeyance",
  UNKNOWN: "Unknown",
}

export const APPLICABILITY_LABELS: Record<ApplicabilityStatus, string> = {
  APPLICABLE: "Applicable",
  NOT_APPLICABLE: "Not applicable",
  UNKNOWN: "Unknown",
}

export const MODIFICATION_TYPE_LABELS: Record<ModificationType, string> = {
  PRODUCTION: "Production",
  IN_SERVICE: "In service",
}

export const MOD_NATURE_LABELS: Record<ModNature, string> = {
  MAINTENANCE: "Maintenance",
  INSPECTION: "Inspection",
  OTHER: "Other",
}

export const ASSOCIATION_LABELS: Record<
  AppAircraftModStatusSchemasAircraftAssociation,
  string
> = {
  AIRFRAME: "Airframe",
  ENGINE: "Engine",
  APU: "APU",
  EQUIPMENT: "Equipment",
  APPLIANCES: "Appliances",
  OTHER: "Other",
}

export const DATA_SOURCE_LABELS: Record<ModDataSource, string> = {
  C_CHECK_RECORDS: "C-check records",
  OEM_RELEASE: "OEM release",
  CAMO_ARCHIVE: "CAMO archive",
  LESSOR_RECORDS: "Lessor records",
  OTHER: "Other",
}

export const modStatusSchema = z.enum([
  "SB_SCHEDULED",
  "SB_REJECTED",
  "SB_FULLY_EMBODIED",
  "SB_PARTIALLY_EMBODIED",
  "SB_NOT_YET_ISSUED",
  "MOD_NOT_APPLICABLE_IN_SB_SOLUTION",
  "MOD_DONE_IN_PRODUCTION",
  "MOD_PLANNED_IN_PRODUCTION",
  "EQUIVALENT_MOD_FACTORY_INCORPORATED",
] as const satisfies readonly ModStatusEnum[])

export const complianceTypeSchema = z.enum([
  "MANDATORY",
  "OPTIONAL",
  "DESIRABLE",
  "RECOMMENDED",
  "IN_ABEYANCE",
  "UNKNOWN",
] as const satisfies readonly SbComplianceType[])

export const MOD_STATUSES = modStatusSchema.options

export const COMPLIANCE_TYPES = complianceTypeSchema.options

export const modSearchFields = {
  mod_search: z.string().optional().catch(undefined),
  mod_status: z.array(modStatusSchema).optional().catch(undefined),
  mod_compliance: z.array(complianceTypeSchema).optional().catch(undefined),
  mod_period: periodSchema.optional().catch(undefined),
  mod_from: z.string().optional().catch(undefined),
  mod_to: z.string().optional().catch(undefined),
  mod_columns: z.array(modColumnSchema).optional().catch(undefined),
  mod_item: z.string().optional().catch(undefined),
}

const modSearchSchema = z.object(modSearchFields)

export type ModSearch = z.infer<typeof modSearchSchema>

export type ModFilterValues = Pick<
  ModSearch,
  "mod_status" | "mod_compliance" | "mod_period" | "mod_from" | "mod_to"
>

export const NO_MOD_FILTERS: ModFilterValues = {
  mod_status: undefined,
  mod_compliance: undefined,
  mod_period: undefined,
  mod_from: undefined,
  mod_to: undefined,
}

export function hasModFilters(values: ModFilterValues): boolean {
  return Boolean(
    values.mod_status?.length ||
      values.mod_compliance?.length ||
      values.mod_period,
  )
}

export function parseModStatuses(
  values: FilterOptionValue[] | undefined,
): ModStatusEnum[] | undefined {
  return values?.flatMap((value) => {
    const parsed = modStatusSchema.safeParse(value)
    return parsed.success ? [parsed.data] : []
  })
}

export function parseComplianceTypes(
  values: FilterOptionValue[] | undefined,
): SbComplianceType[] | undefined {
  return values?.flatMap((value) => {
    const parsed = complianceTypeSchema.safeParse(value)
    return parsed.success ? [parsed.data] : []
  })
}

export function modColumnsOf(search: ModSearch): ModColumn[] {
  return search.mod_columns
    ? withFixed(MOD_COLUMNS, "number", search.mod_columns)
    : [...MOD_COLUMNS]
}

export function modStatusPagesParams(
  aircraftId: string,
  search: ModSearch,
): ModStatusPagesParams {
  const embodied = periodRange(
    search.mod_period,
    search.mod_from,
    search.mod_to,
  )
  return {
    aircraft_id: aircraftId,
    search: search.mod_search,
    status: search.mod_status,
    compliance_type: search.mod_compliance,
    embodied_from: embodied.from,
    embodied_to: embodied.to,
  }
}

export function modStatusLabel(row: ModStatusPublic): string {
  if (row.mod_number) return `Mod ${row.mod_number}`
  if (row.sb_number) return `SB ${row.sb_number}`
  return "Modification"
}

export function modStatusTitle(row: ModStatusPublic): string {
  return row.mod_title || row.sb_title || modStatusLabel(row)
}
